import React, { useState, useEffect } from 'react';
import { AUDIT_ACTION_LABELS, getAuditLogs } from '../services/auditService';
import { getAllBarbershops } from '../services/barbershopService';
import { getAllUsers } from '../services/userService';
import toast from 'react-hot-toast';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Componente: AdminLogsPage
 * Página de logs de auditoria com filtros e exportação PDF
 */
const AdminLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [barbershops, setBarbershops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const logsPerPage = 20;

  // Filtros
  const [filters, setFilters] = useState({
    barbershopId: '',
    action: '',
    startDate: '',
    endDate: '',
    searchTerm: ''
  });

  // Mapear ações para nomes legíveis
  const legacyActionLabels = {
    create_barbershop: 'Criar Barbearia',
    update_barbershop: 'Atualizar Barbearia',
    delete_barbershop: 'Deletar Barbearia',
    create_user: 'Criar Usuário',
    update_user: 'Atualizar Usuário',
    delete_user: 'Deletar Usuário',
    change_user_role: 'Alterar Permissão',
    create_appointment: 'Criar Agendamento',
    update_appointment: 'Atualizar Agendamento',
    cancel_appointment: 'Cancelar Agendamento',
    confirm_appointment: 'Confirmar Agendamento',
    complete_appointment: 'Completar Agendamento',
    no_show_appointment: 'Marcar Não Compareceu',
    create_slot: 'Criar Horário',
    create_bulk_slots: 'Criar Horários em Lote',
    delete_slot: 'Deletar Horário',
    update_services: 'Atualizar Serviços',
    create_review: 'Criar Avaliação',
    delete_review: 'Deletar Avaliação'
  };
  const actionLabels = { ...legacyActionLabels, ...AUDIT_ACTION_LABELS };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logs, filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [logsResult, barbershopsResult, usersResult] = await Promise.all([
        getAuditLogs(),
        getAllBarbershops(),
        getAllUsers()
      ]);

      if (logsResult.success) {
        const auditLogs = logsResult.data;
        const auditedRegistrations = new Set(
          auditLogs
            .filter(log => log.action === 'register' || log.action === 'create_user')
            .map(log => log.targetId || log.userId)
        );
        const recoveredRegistrations = usersResult.success
          ? usersResult.data
              .filter(user => user.createdAt && !auditedRegistrations.has(user.id))
              .map(user => ({
                id: `recovered-register-${user.id}`,
                action: 'register_recovered',
                userId: user.id,
                userName: user.name || user.email || 'Usuário',
                userEmail: user.email || '',
                userRole: user.role || 'user',
                targetType: 'user',
                targetId: user.id,
                targetName: user.name || user.email || 'Usuário',
                timestamp: user.createdAt,
                description: 'Cadastro recuperado do perfil existente (anterior aos logs de autenticação)',
                metadata: { source: 'users.createdAt', recovered: true }
              }))
          : [];

        setLogs(
          [...auditLogs, ...recoveredRegistrations]
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        );
      }

      if (barbershopsResult.success) {
        setBarbershops(barbershopsResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar logs');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Filtro por barbearia
    if (filters.barbershopId) {
      filtered = filtered.filter(log => 
        (log.targetType === 'barbershop' && log.targetId === filters.barbershopId) ||
        log.metadata?.barbershopId === filters.barbershopId
      );
    }

    // Filtro por ação
    if (filters.action) {
      filtered = filtered.filter(log => log.action === filters.action);
    }

    // Filtro por data
    if (filters.startDate) {
      filtered = filtered.filter(log => 
        new Date(log.timestamp) >= new Date(filters.startDate)
      );
    }
    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(log => 
        new Date(log.timestamp) <= endDate
      );
    }

    // Busca por texto
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(log => 
        log.userName?.toLowerCase().includes(term) ||
        log.userEmail?.toLowerCase().includes(term) ||
        log.userId?.toLowerCase().includes(term) ||
        log.targetName?.toLowerCase().includes(term) ||
        log.description?.toLowerCase().includes(term)
      );
    }

    setFilteredLogs(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      barbershopId: '',
      action: '',
      startDate: '',
      endDate: '',
      searchTerm: ''
    });
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(18);
    doc.text('Relatório de Logs de Auditoria', 14, 22);
    
    // Data de geração
    doc.setFontSize(11);
    doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 30);
    
    // Filtros aplicados
    let yPos = 38;
    if (filters.barbershopId || filters.action || filters.startDate || filters.endDate) {
      doc.setFontSize(10);
      doc.text('Filtros aplicados:', 14, yPos);
      yPos += 6;
      
      if (filters.barbershopId) {
        const barbershop = barbershops.find(b => b.id === filters.barbershopId);
        doc.text(`• Barbearia: ${barbershop?.name || 'N/A'}`, 14, yPos);
        yPos += 5;
      }
      if (filters.action) {
        doc.text(`• Ação: ${actionLabels[filters.action] || filters.action}`, 14, yPos);
        yPos += 5;
      }
      if (filters.startDate) {
        doc.text(`• Data inicial: ${new Date(filters.startDate).toLocaleDateString('pt-BR')}`, 14, yPos);
        yPos += 5;
      }
      if (filters.endDate) {
        doc.text(`• Data final: ${new Date(filters.endDate).toLocaleDateString('pt-BR')}`, 14, yPos);
        yPos += 5;
      }
      yPos += 3;
    }
    
    // Preparar dados para tabela
    const tableData = filteredLogs.map(log => [
      new Date(log.timestamp).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      log.userName || 'N/A',
      actionLabels[log.action] || log.action,
      log.targetName || log.targetType || 'N/A',
      log.description || ''
    ]);
    
    // Criar tabela
    doc.autoTable({
      startY: yPos,
      head: [['Data/Hora', 'Usuário', 'Ação', 'Alvo', 'Descrição']],
      body: tableData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [79, 70, 229] },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 'auto' }
      },
      margin: { top: 10 }
    });
    
    // Total de registros
    const finalY = doc.lastAutoTable.finalY || yPos;
    doc.setFontSize(10);
    doc.text(`Total de registros: ${filteredLogs.length}`, 14, finalY + 10);
    
    // Salvar PDF
    const filename = `logs_auditoria_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    
    toast.success('PDF exportado com sucesso!');
  };

  // Paginação
  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);
  const totalPages = Math.ceil(filteredLogs.length / logsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Logs de Auditoria</h1>
          <p className="mt-2 text-sm text-gray-600">
            Histórico completo de ações do sistema
          </p>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {/* Busca por texto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Buscar
              </label>
              <input
                type="text"
                value={filters.searchTerm}
                onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                placeholder="Nome, e-mail, UID, alvo ou descrição..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Filtro por barbearia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Barbearia
              </label>
              <select
                value={filters.barbershopId}
                onChange={(e) => handleFilterChange('barbershopId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Todas</option>
                {barbershops.map(barbershop => (
                  <option key={barbershop.id} value={barbershop.id}>
                    {barbershop.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por ação */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Ação
              </label>
              <select
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Todas</option>
                {Object.entries(actionLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            {/* Data inicial */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Inicial
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Data final */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Final
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          {/* Botões de ação */}
          <div className="flex flex-col sm:flex-row gap-2 mt-4">
            <button
              onClick={clearFilters}
              className="w-full sm:w-auto px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
            >
              Limpar Filtros
            </button>
            <button
              onClick={exportToPDF}
              disabled={filteredLogs.length === 0}
              className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
            >
              📄 Exportar PDF
            </button>
          </div>

          {/* Contador de resultados */}
          <div className="text-sm text-gray-600 mt-4">
            {filteredLogs.length} registro{filteredLogs.length !== 1 ? 's' : ''} encontrado{filteredLogs.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Tabela de Logs */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data/Hora
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuário
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ação
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alvo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Nenhum log encontrado
                    </td>
                  </tr>
                ) : (
                  currentLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(log.timestamp).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {log.userName || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {log.userEmail || log.userRole}
                        </div>
                        <div className="text-xs text-gray-400" title={log.userId}>
                          UID: {log.userId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-indigo-100 text-indigo-800">
                          {actionLabels[log.action] || log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.targetName || log.targetType || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                        {log.description || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Próximo
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Mostrando <span className="font-medium">{indexOfFirstLog + 1}</span> a{' '}
                    <span className="font-medium">
                      {Math.min(indexOfLastLog, filteredLogs.length)}
                    </span>{' '}
                    de <span className="font-medium">{filteredLogs.length}</span> resultados
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      ←
                    </button>
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === pageNumber
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      } else if (
                        pageNumber === currentPage - 2 ||
                        pageNumber === currentPage + 2
                      ) {
                        return (
                          <span
                            key={pageNumber}
                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                          >
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      →
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogsPage;
